import { useGetList, type Identifier } from "ra-core";
import { useEffect, useId, useMemo } from "react";

import { getSupabaseClient } from "../../providers/supabase/supabase";
import { groupNotes, type ClientNote } from "./clientNotes";

/**
 * Team notes of some contacts (or of everybody when no ids are given), kept
 * live: a note written by Mica shows up on Javi's screen at once.
 */
export const useClientNotes = (contactIds?: Identifier[]) => {
  // null = everybody; "" = nobody (nothing to load)
  const idList = contactIds
    ? [...new Set(contactIds.map((id) => String(id)))].sort().join(",")
    : null;
  const filter = idList != null ? { "contact_id@in": `(${idList})` } : {};
  const { data = [], refetch } = useGetList<ClientNote>(
    "contact_notes",
    {
      filter,
      pagination: { page: 1, perPage: 5000 },
      sort: { field: "date", order: "DESC" },
    },
    { enabled: idList !== "", refetchInterval: 60_000 },
  );

  const channelName = `client-notes-${useId()}`;
  useEffect(() => {
    const supabase = getSupabaseClient();
    const channel = supabase
      .channel(channelName)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "contact_notes" },
        () => refetch(),
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [channelName, refetch]);

  const notesOf = useMemo(() => groupNotes(data), [data]);
  return { notesOf, refetch };
};
